import type { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { meQuery, orgQuery } from './api/queries';
import { queryClient } from './api/queryClient';
import { SignInPage } from './auth/SignInPage';
import { RequireAuth } from './app/RequireAuth';
import { RootRedirect } from './app/RootRedirect';
import { FullPageLoader } from './app/FullPageLoader';
import { NotFoundPage, RouteError } from './app/NotFoundPage';

// Sign-in and the redirects around it ship in the entry chunk; every other
// screen is fetched when first visited, so a share link or the sign-in page
// doesn't download the whole console. The router loads every matched route's
// module at once (the console shell and its page in parallel) and keeps the
// current page up meanwhile.
/**
 * Start the session check (and the org, once signed in) while the screen's
 * code downloads; the screens read the same queries from the cache. Never
 * throws and never waits: RequireAuth still decides about signing in.
 */
function prefetchSession(qc: QueryClient, orgId?: string) {
  void qc.ensureQueryData(meQuery).then(
    (me) => {
      if (me && orgId) void qc.prefetchQuery(orgQuery(orgId));
    },
    () => undefined,
  );
  return null;
}

export const createRoutes = (qc: QueryClient): RouteObject[] => [
  {
    errorElement: <RouteError />,
    // Shown on first load until the matched routes' code arrives.
    HydrateFallback: FullPageLoader,
    children: [
      { path: '/', element: <RootRedirect /> },
      { path: '/signin', element: <SignInPage /> },
      { path: '/auth/callback', lazy: async () => ({ Component: (await import('./auth/AuthCallbackPage')).AuthCallbackPage }) },
      { path: '/attest/callback', lazy: async () => ({ Component: (await import('./auth/AttestCallbackPage')).AttestCallbackPage }) },
      {
        path: '/invite/:token',
        loader: () => prefetchSession(qc),
        lazy: async () => ({ Component: (await import('./auth/InvitePage')).InvitePage }),
      },
      { path: '/verify/:token', lazy: async () => ({ Component: (await import('./features/verify/SharedProofPage')).SharedProofPage }) },
      {
        path: '/welcome',
        loader: () => prefetchSession(qc),
        lazy: async () => {
          const { WelcomePage } = await import('./app/WelcomePage');
          return {
            element: (
              <RequireAuth>
                <WelcomePage />
              </RequireAuth>
            ),
          };
        },
      },
      {
        path: '/portfolio',
        loader: () => prefetchSession(qc),
        lazy: async () => {
          const { PortfolioPage } = await import('./features/portfolio/PortfolioPage');
          return {
            element: (
              <RequireAuth>
                <PortfolioPage />
              </RequireAuth>
            ),
          };
        },
      },
      {
        path: '/orgs/:orgId',
        loader: ({ params }) => prefetchSession(qc, params.orgId),
        lazy: async () => {
          const { OrgLayout } = await import('./app/AppShell');
          return {
            element: (
              <RequireAuth>
                <OrgLayout />
              </RequireAuth>
            ),
          };
        },
        children: [
          { index: true, lazy: async () => ({ Component: (await import('./features/overview/OverviewPage')).OverviewPage }) },
          { path: 'agents', lazy: async () => ({ Component: (await import('./features/agents/AgentsPage')).AgentsPage }) },
          { path: 'agents/:agentId', lazy: async () => ({ Component: (await import('./features/agents/AgentDetailPage')).AgentDetailPage }) },
          { path: 'policy', lazy: async () => ({ Component: (await import('./features/policy/PolicyPage')).PolicyPage }) },
          { path: 'events', lazy: async () => ({ Component: (await import('./features/events/EventsPage')).EventsPage }) },
          { path: 'events/:eventId', lazy: async () => ({ Component: (await import('./features/events/EventPage')).EventPage }) },
          { path: 'events/:eventId/proof', lazy: async () => ({ Component: (await import('./features/proof/ProofPage')).ProofPage }) },
          { path: 'alerts', lazy: async () => ({ Component: (await import('./features/alerts/AlertsPage')).AlertsPage }) },
          { path: 'alerts/:alertId', lazy: async () => ({ Component: (await import('./features/alerts/AlertsPage')).AlertsPage }) },
          { path: 'anchors', lazy: async () => ({ Component: (await import('./features/anchors/AnchorsPage')).AnchorsPage }) },
          { path: 'members', lazy: async () => ({ Component: (await import('./features/members/MembersPage')).MembersPage }) },
          { path: 'settings', lazy: async () => ({ Component: (await import('./features/settings/SettingsPage')).SettingsPage }) },
          { path: '*', element: <NotFoundPage inShell /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(createRoutes(queryClient));
